import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/integrations/supabase/client'
import SEO from '@/components/common/SEO'

const M = '#0d0d1a'
const G = '#d4a843'
const BR = 'rgba(255,255,255,0.08)'
const CA = 'rgba(255,255,255,0.04)'
const MU = 'rgba(255,255,255,0.45)'
const TX = 'rgba(255,255,255,0.88)'

const SITE_URL = 'https://still-waters-university.vercel.app'

export default function SignUpPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { signUp, signIn, user, isLoading: authLoading } = useAuth()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [oauthLoading, setOauthLoading] = useState<'google' | 'facebook' | null>(null)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const [searchParams] = useSearchParams()
  const nextPath = searchParams.get('next') || '/dashboard'

  useEffect(() => {
    if (!authLoading && user) navigate(nextPath, { replace: true })
  }, [user, authLoading, navigate, nextPath])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')
    const { data, error } = await signUp(email, password, { name, full_name: name })

    if (error) {
      if (error.message.includes('already registered')) {
        // Try silent login
        console.log('[SIGNUP] User already exists, attempting silent login...')
        const { data: signInData, error: signInError } = await signIn(email, password)
        if (!signInError && signInData?.user) {
          console.log('[SIGNUP] Silent login successful')
          navigate(nextPath, { replace: true })
          return
        }
        setError('An account with this email already exists. Please sign in with your password.')
      } else {
        setError(error.message || t('university.auth.error', 'Something went wrong.'))
      }
      setLoading(false)
    } else if (data && data.user && data.user.email_confirmed_at) {
      // Already confirmed (linked or existing with no enumeration protection)
      navigate(nextPath, { replace: true })
    } else if (data && data.user === null && !error) {
      // Exist but no session
      console.log('[SIGNUP] User already exists (null user), attempting silent login...')
      const { data: signInData, error: signInError } = await signIn(email, password)
      if (!signInError && signInData?.user) {
        console.log('[SIGNUP] Silent login successful')
        navigate(nextPath, { replace: true })
        return
      }
      setError('An account with this email already exists. Please sign in with your password.')
      setLoading(false)
    } else {
      setDone(true)
    }
  }

  async function handleOAuth(provider: 'google' | 'facebook') {
    setOauthLoading(provider)
    setError('')
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        // We include the nextPath so it's preserved in the URL when they return.
        redirectTo: `${SITE_URL}/signin?next=${encodeURIComponent(nextPath)}`,
        queryParams: provider === 'google' ? { prompt: 'select_account' } : {},
      },
    })
    if (error) { setError(error.message); setOauthLoading(null) }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '13px 16px',
    background: CA, border: `1.5px solid ${BR}`,
    borderRadius: 11, color: TX, fontSize: 15, outline: 'none',
    fontFamily: "'Inter', sans-serif", boxSizing: 'border-box', transition: 'border-color 0.2s',
  }

  if (done) return (
    <div style={{ background: M, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: "'Inter', sans-serif" }}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ textAlign: 'center', maxWidth: 400 }}>
        <div style={{ fontSize: 52, marginBottom: 20 }}>📬</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 12 }}>
          {t('university.auth.checkEmail', 'Check your email')}
        </h2>
        <p style={{ color: MU, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
          {t('university.auth.confirmSent', `We sent a confirmation link to ${email}. Click it to activate your account, then come back to sign in.`)}
        </p>
        <motion.button whileHover={{ scale: 1.02 }} onClick={() => navigate('/signin')}
          style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#0d0d1a', border: 'none', borderRadius: 12, padding: '13px 32px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          {t('university.auth.goToSignIn', 'Go to Sign In')}
        </motion.button>
      </motion.div>
    </div>
  )

  return (
    <div style={{ background: M, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: "'Inter', sans-serif" }}>
      <SEO title="Create Account" noindex />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ width: '100%', maxWidth: 420 }}>

        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <img src="/logo.png" alt="Still Waters University"
            style={{ width: 72, height: 72, borderRadius: '50%', marginBottom: 16, boxShadow: '0 4px 24px rgba(212,168,67,0.3)' }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, color: '#fff', margin: '0 0 6px' }}>
            Create Your Account
          </h1>
          <p style={{ color: MU, fontSize: 14 }}>{t('university.auth.freeToStart', 'Free to start — no credit card needed')}</p>
        </div>

        <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 20, padding: '32px' }}>

          {/* OAuth */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
              onClick={() => handleOAuth('google')} disabled={!!oauthLoading || loading}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, background: '#fff', color: '#1f1f1f', border: 'none', borderRadius: 12, padding: '13px 16px', fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: oauthLoading === 'facebook' ? 0.5 : 1 }}>
              {oauthLoading === 'google' ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
                  style={{ width: 20, height: 20, border: '2px solid #ccc', borderTopColor: '#1f1f1f', borderRadius: '50%' }} />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
              )}
              {oauthLoading === 'google' ? 'Signing in…' : t('university.auth.continueGoogle', 'Continue with Google')}
            </motion.button>

            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
              onClick={() => handleOAuth('facebook')} disabled={!!oauthLoading || loading}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, background: '#1877F2', color: '#fff', border: 'none', borderRadius: 12, padding: '13px 16px', fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: oauthLoading === 'google' ? 0.5 : 1 }}>
              {oauthLoading === 'facebook' ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
                  style={{ width: 20, height: 20, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%' }} />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              )}
              {oauthLoading === 'facebook' ? 'Signing in…' : t('university.auth.continueFacebook', 'Continue with Facebook')}
            </motion.button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <div style={{ flex: 1, height: 1, background: BR }} />
            <span style={{ fontSize: 12, color: MU, whiteSpace: 'nowrap' }}>{t('university.auth.orEmail', 'or create with email')}</span>
            <div style={{ flex: 1, height: 1, background: BR }} />
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>Full Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name"
                required style={inputStyle} onFocus={e => (e.target.style.borderColor = G)} onBlur={e => (e.target.style.borderColor = BR)} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>
                {t('university.auth.email', 'Email address')}
              </label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com"
                required style={inputStyle} onFocus={e => (e.target.style.borderColor = G)} onBlur={e => (e.target.style.borderColor = BR)} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>
                {t('university.auth.password', 'Password')}
              </label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 8 characters"
                minLength={8} required style={inputStyle} onFocus={e => (e.target.style.borderColor = G)} onBlur={e => (e.target.style.borderColor = BR)} />
            </div>

            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#f87171' }}>
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <motion.button type="submit" disabled={loading || !!oauthLoading}
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#0d0d1a', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: loading || oauthLoading ? 0.7 : 1 }}>
              {loading ? 'Creating account…' : t('university.auth.createAccount', 'Create Account')}
            </motion.button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 22, fontSize: 13, color: MU }}>
            {t('university.auth.hasAccount', 'Already have an account?')}{' '}
            <button onClick={() => navigate(`/signin?next=${encodeURIComponent(nextPath)}`)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: G, fontWeight: 600, fontSize: 13, padding: 0 }}>
              {t('university.auth.signIn', 'Sign In')}
            </button>
          </p>
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 11, color: 'rgba(255,255,255,0.2)', lineHeight: 1.6 }}>
          {t('university.auth.sageNote', 'Use the same account as the Sage app — your login works across both.')}
        </p>
      </motion.div>
    </div>
  )
}